import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Mail, TrendingUp, TrendingDown, Minus, Printer } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { getSnapshotReport, emailSnapshotReport } from "../../lib/api";
import { useAuth } from "../../App";
import BulkDistrictSnapshotDialog from "./BulkDistrictSnapshotDialog";

const TREND = {
  better: { label: "Improving", cls: "text-green-700", Icon: TrendingUp },
  worse: { label: "Declining", cls: "text-red-700", Icon: TrendingDown },
  same: { label: "Steady", cls: "text-slate-500", Icon: Minus },
  na: { label: "No data", cls: "text-slate-400", Icon: Minus },
};

function TrendBadge({ dir }) {
  const t = TREND[dir] || TREND.na;
  const { Icon } = t;
  return (
    <span className={`inline-flex items-center gap-1 font-semibold ${t.cls}`}>
      <Icon className="h-3.5 w-3.5" />{t.label}
    </span>
  );
}

// Assessment indicators (match the Assessment / Advisory cards)
const CATS = [
  { key: "participation_level", label: "Att" },
  { key: "engagement", label: "Eng" },
  { key: "communication", label: "Com" },
  { key: "behavior", label: "Beh" },
  { key: "attention", label: "Atn" },
  { key: "work_completion", label: "Task" },
];

function scoreColor(v) {
  if (v == null) return "bg-gray-50 text-gray-400";
  if (v >= 3) return "bg-green-100 text-green-800";
  if (v >= 2) return "bg-yellow-100 text-yellow-800";
  return "bg-red-100 text-red-800";
}

// Colorful per-indicator summary (like the Advisory/Assessment cards). `prev`
// (optional) renders a muted previous-period value under each score.
function IndicatorCells({ averages, prev = null, testIdPrefix }) {
  const avgVals = CATS.map(c => averages?.[c.key]).filter(v => v != null);
  const overall = avgVals.length ? Math.round(avgVals.reduce((a, b) => a + b, 0) / avgVals.length * 10) / 10 : null;
  const cells = [...CATS, { key: "__avg__", label: "Avg" }];
  return (
    <div className="grid grid-cols-7 gap-1" data-testid={testIdPrefix}>
      {cells.map(c => {
        const v = c.key === "__avg__" ? overall : averages?.[c.key];
        const p = c.key === "__avg__" ? null : prev?.[c.key];
        const isAvg = c.key === "__avg__";
        return (
          <div key={c.key} className={`rounded text-center py-1 ${scoreColor(v)} ${isAvg ? "font-bold ring-1 ring-black/5" : "font-semibold"}`} data-testid={`${testIdPrefix}-${c.key}`}>
            <div className="text-[10px] uppercase tracking-wide opacity-70">{c.label}</div>
            <div className="text-sm leading-tight">{v != null ? v.toFixed(1) : "—"}</div>
            {prev && !isAvg && <div className="text-[9px] text-gray-500 leading-none">prev {p != null ? p.toFixed(1) : "—"}</div>}
          </div>
        );
      })}
    </div>
  );
}

export default function SnapshotReportTab({ students, formerStudents, districts = [], semesterDates, lockedStudentId = null }) {
  const { isAdmin, hasRole } = useAuth();
  const canEmail = isAdmin() || hasRole("Clinician");
  const [snapshotData, setSnapshotData] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [snapshotStudentFilter, setSnapshotStudentFilter] = useState(lockedStudentId || "all");
  const [snapshotStatusFilter, setSnapshotStatusFilter] = useState(lockedStudentId ? "all" : "current");
  const [snapshotSemesterFilter, setSnapshotSemesterFilter] = useState("ytd");
  const [snapshotDistrictFilter, setSnapshotDistrictFilter] = useState("all");
  const [includeLastWeek, setIncludeLastWeek] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailNote, setEmailNote] = useState("");
  const [saveDistrictEmail, setSaveDistrictEmail] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const reportRef = useRef(null);

  // Last-week summary is only computed for a narrow scope (one student or district).
  const narrowScope = (!!lockedStudentId || snapshotStudentFilter !== "all" || snapshotDistrictFilter !== "all");

  const fetchSnapshot = async () => {
    setSnapshotLoading(true);
    try {
      const params = { student_status: snapshotStatusFilter };
      if (snapshotStudentFilter !== "all") params.student_id = snapshotStudentFilter;
      if (snapshotDistrictFilter !== "all") params.district = snapshotDistrictFilter;
      if (includeLastWeek && narrowScope) params.include_last_week = true;
      if (snapshotSemesterFilter !== "ytd") {
        const sem = semesterDates.find(s => s.id === snapshotSemesterFilter);
        if (sem) { params.start_date = sem.start_date; params.end_date = sem.end_date; }
      }
      const res = await getSnapshotReport(params);
      setSnapshotData(res.data);
    } catch { toast.error("Failed to load snapshot report"); }
    finally { setSnapshotLoading(false); }
  };

  // When embedded in a student file (locked to one student), auto-load on mount and when the period changes.
  useEffect(() => {
    if (lockedStudentId) fetchSnapshot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedStudentId, snapshotSemesterFilter, includeLastWeek]);

  const currentList = snapshotStatusFilter === "former" ? formerStudents : snapshotStatusFilter === "all" ? [...students, ...formerStudents] : students;

  const selectedSem = semesterDates.find(s => s.id === snapshotSemesterFilter);
  const isSummer = !!(selectedSem && (selectedSem.id === "summer" || (selectedSem.name || "").toLowerCase().includes("summer")));

  // Resolve the district name + its saved contact email for the email flow.
  const selectedStudentObj = snapshotStudentFilter !== "all" ? currentList.find(s => s.id === snapshotStudentFilter) : null;
  const scopeDistrictName = snapshotDistrictFilter !== "all"
    ? snapshotDistrictFilter
    : (selectedStudentObj?.school_district || "").trim();
  const scopeDistrictRec = scopeDistrictName
    ? districts.find(d => (d.name || "").trim().toLowerCase() === scopeDistrictName.toLowerCase())
    : null;
  const canOpenEmail = canEmail && (snapshotDistrictFilter !== "all" || !!selectedStudentObj);

  // The bulk "Send to all Districts" action is Admin-only and only applies when
  // viewing ALL current students (not a single student, former, or one district).
  const allCurrentSelected = !lockedStudentId && isAdmin()
    && snapshotStatusFilter === "current" && snapshotStudentFilter === "all" && snapshotDistrictFilter === "all";
  const bulkStartEnd = snapshotSemesterFilter !== "ytd"
    ? semesterDates.find(s => s.id === snapshotSemesterFilter) : null;

  const openEmailDialog = () => {
    setEmailTo(scopeDistrictRec?.contact_email || "");
    setEmailNote("");
    setSaveDistrictEmail(false);
    setEmailOpen(true);
  };

  // Human label for the selected period (semester name, or the school year for YTD).
  const periodLabelText = () => {
    if (snapshotSemesterFilter !== "ytd") return selectedSem?.name || selectedSem?.id || "Selected Period";
    const ds = (semesterDates || []).flatMap(s => [s.start_date, s.end_date]).filter(Boolean);
    const ys = ds.map(d => parseInt(String(d).slice(0, 4))).filter(Boolean);
    if (ys.length) {
      const mn = Math.min(...ys), mx = Math.max(...ys);
      return `School Year ${mn === mx ? mn : mn + "\u2013" + mx} (Year to Date)`;
    }
    return "Full School Year (Year to Date)";
  };

  // Print the snapshot exactly as rendered on screen (for meetings / hand-outs).
  const handlePrint = () => {
    const node = reportRef.current;
    if (!node) { toast.error("Generate the report first"); return; }
    const win = window.open("", "_blank", "width=1180,height=820");
    if (!win) { toast.error("Allow pop-ups for this site to print the report"); return; }

    const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
      .map(el => el.outerHTML).join("\n");
    const scope = snapshotDistrictFilter !== "all"
      ? snapshotDistrictFilter
      : (selectedStudentObj ? `${selectedStudentObj.last_name}, ${selectedStudentObj.preferred_name || selectedStudentObj.first_name}` : "All Students");
    const generated = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>Weekly Snapshot Report</title>
${styles}
<style>
  @page { size: landscape; margin: 10mm; }
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  html, body { background: #ffffff; }
  body { padding: 20px; }
  .print-head { margin: 0 0 18px; border-bottom: 2px solid #00305E; padding-bottom: 10px; }
  .print-head h1 { font-size: 22px; font-weight: 800; color: #00305E; margin: 0; letter-spacing: .3px; }
  .print-head .meta { font-size: 12px; color: #475569; margin-top: 4px; }
  [data-testid^="snapshot-student-"], [data-testid="snapshot-school-summary"] { break-inside: avoid; page-break-inside: avoid; }
</style></head><body>
<div class="print-head">
  <h1>Weekly Snapshot Report</h1>
  <div class="meta">${scope} &nbsp;&middot;&nbsp; ${periodLabelText()} &nbsp;&middot;&nbsp; Generated ${generated}</div>
</div>
${node.outerHTML}
</body></html>`);
    win.document.close();
    // Give linked stylesheets a moment to load before printing.
    setTimeout(() => { win.focus(); win.print(); }, 500);
  };

  const sendEmail = async () => {
    const to = emailTo.trim();
    if (!to) { toast.error("Enter a recipient email"); return; }
    setEmailSending(true);
    try {
      const payload = {
        recipient_email: to,
        student_status: snapshotStatusFilter,
        include_last_week: !!(includeLastWeek && narrowScope),
        note: emailNote.trim(),
        save_district_email: !!(saveDistrictEmail && scopeDistrictName),
      };
      if (snapshotStudentFilter !== "all") payload.student_id = snapshotStudentFilter;
      if (snapshotDistrictFilter !== "all") payload.district = snapshotDistrictFilter;
      else if (scopeDistrictName) payload.district = scopeDistrictName;
      if (snapshotSemesterFilter !== "ytd") {
        const sem = semesterDates.find(s => s.id === snapshotSemesterFilter);
        if (sem) { payload.start_date = sem.start_date; payload.end_date = sem.end_date; }
      }
      payload.period_label = periodLabelText();
      const res = await emailSnapshotReport(payload);
      if (res.data?.suppressed) toast.success("Report queued — email sending is off in preview; it will send on the live site.");
      else toast.success(`Snapshot emailed to ${res.data?.recipient || to}`);
      setEmailOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to email the report");
    } finally { setEmailSending(false); }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div><CardTitle>Weekly Snapshot Report</CardTitle><CardDescription>Behavior + Attendance data per student</CardDescription></div>
          <div className="flex items-center gap-2">
            {allCurrentSelected && (
              <Button size="sm" variant="outline" className="border-[#00305E] text-[#00305E] hover:bg-[#00305E]/5" onClick={() => setBulkOpen(true)} data-testid="bulk-snapshot-btn" title="Email each district their own students' Weekly Snapshot">
                <Mail className="h-4 w-4 mr-1.5" />Send Weekly Snapshot to all Districts
              </Button>
            )}
            {!lockedStudentId && canEmail && (
              <Button size="sm" variant="outline" disabled={!canOpenEmail} onClick={openEmailDialog} data-testid="email-snapshot-btn" title={canOpenEmail ? "Email this snapshot to the district contact" : "Select a district or a single student first"}>
                <Mail className="h-4 w-4 mr-1.5" />Email to District
              </Button>
            )}
            <Button size="sm" variant="outline" disabled={!snapshotData || !snapshotData.students?.length} onClick={handlePrint} data-testid="print-snapshot-btn" title={snapshotData?.students?.length ? "Print this snapshot as shown on screen" : "Generate the report first"}>
              <Printer className="h-4 w-4 mr-1.5" />Print
            </Button>
            <Button size="sm" className="bg-[#00305E] hover:bg-[#00305E]/90" disabled={snapshotLoading} onClick={fetchSnapshot} data-testid="load-snapshot-btn">{snapshotLoading ? "Loading..." : "Generate Report"}</Button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-3">
          {!lockedStudentId && (
          <Select value={snapshotStatusFilter} onValueChange={(v) => { setSnapshotStatusFilter(v); setSnapshotStudentFilter("all"); }}>
            <SelectTrigger className="w-[160px]" data-testid="snapshot-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="current">Current Students</SelectItem><SelectItem value="former">Former Students</SelectItem><SelectItem value="all">All Students</SelectItem></SelectContent>
          </Select>
          )}
          {!lockedStudentId && (
          <Select value={snapshotDistrictFilter} onValueChange={setSnapshotDistrictFilter}>
            <SelectTrigger className="w-[220px]" data-testid="snapshot-district-filter"><SelectValue placeholder="All districts" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Districts</SelectItem>
              {[...districts].sort((a, b) => (a.name || "").localeCompare(b.name || "")).filter(d => (d.name || "").trim()).map(d => (<SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>))}
            </SelectContent>
          </Select>
          )}
          {!lockedStudentId && (
          <Select value={snapshotStudentFilter} onValueChange={setSnapshotStudentFilter}>
            <SelectTrigger className="w-[220px]" data-testid="snapshot-student-filter"><SelectValue placeholder="All students" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Students</SelectItem>
              {[...currentList].sort((a, b) => a.last_name.localeCompare(b.last_name)).map(s => (<SelectItem key={s.id} value={s.id}>{s.last_name}, {s.preferred_name || s.first_name}{s.status === "former" ? " (Former)" : ""}</SelectItem>))}
            </SelectContent>
          </Select>
          )}
          <Select value={snapshotSemesterFilter} onValueChange={setSnapshotSemesterFilter}>
            <SelectTrigger className="w-[180px]" data-testid="snapshot-semester-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="ytd">Year to Date</SelectItem>{semesterDates.map(sem => (<SelectItem key={sem.id} value={sem.id}>{sem.name || sem.id}</SelectItem>))}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <Checkbox id="snap-last-week" checked={includeLastWeek} disabled={!narrowScope} onCheckedChange={(v) => setIncludeLastWeek(!!v)} data-testid="snapshot-last-week-checkbox" />
          <Label htmlFor="snap-last-week" className={`text-sm ${narrowScope ? "cursor-pointer" : "text-muted-foreground"}`}>
            Include last-week summary (latest clinical goal + assessment & attendance trend)
          </Label>
          {!narrowScope && <span className="text-xs text-muted-foreground">— select a single student or a district first</span>}
        </div>
      </CardHeader>
      <CardContent>
        {!snapshotData ? (
          <p className="text-center py-12 text-muted-foreground">Click Generate Report to view clinical snapshot data</p>
        ) : snapshotData.students.length === 0 ? (
          <p className="text-center py-12 text-muted-foreground">No student data found</p>
        ) : (
          <div className="space-y-8" data-testid="snapshot-results" ref={reportRef}>
            {snapshotData.students.map(student => (
              <div key={student.student_id} className="border rounded-lg overflow-hidden" data-testid={`snapshot-student-${student.student_id}`}>
                <div className="bg-[#00305E] text-white px-4 py-2 flex items-center justify-between">
                  <span className="font-semibold">{student.student_name}</span>
                  {student.status === "former" && <span className="text-xs bg-amber-500 text-white px-2 py-0.5 rounded">Former{student.end_date ? ` (left ${student.end_date})` : ""}</span>}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4">
                  <div>
                    <h4 className="font-semibold text-sm text-[#00305E] mb-2 uppercase tracking-wide">Behavior</h4>
                    <div className="grid grid-cols-2 gap-1">
                      {snapshotData.behavior_categories.map(cat => (
                        <div key={cat} className="flex justify-between px-2 py-1 text-xs border-b"><span className="text-gray-600 truncate mr-2">{cat}</span><span className={`font-semibold ${student.behavior[cat] > 0 ? 'text-red-600' : 'text-gray-400'}`}>{student.behavior[cat]}</span></div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm text-[#00305E] mb-2 uppercase tracking-wide">Attendance YTD</h4>
                    <div className="grid grid-cols-2 gap-1">
                      {[["Absent Excused", student.attendance_ytd.absent_excused], ["Absent Unexcused", student.attendance_ytd.absent_unexcused], ["Early Dismissal (Unexcused)", student.attendance_ytd.early_dismissal_unexcused], ["Late Arrival (Unexcused)", student.attendance_ytd.late_arrival_unexcused], ["On Pause", student.attendance_ytd.on_pause]].map(([label, val]) => (
                        <div key={label} className="flex justify-between px-2 py-1 text-xs border-b"><span className="text-gray-600">{label}</span><span className={`font-semibold ${val > 0 ? 'text-amber-600' : 'text-gray-400'}`}>{val}</span></div>
                      ))}
                    </div>
                  </div>
                </div>
                {student.assessments && (
                  <div className="px-4 pb-2" data-testid={`snapshot-assessments-${student.student_id}`}>
                    <h4 className="font-semibold text-sm text-[#00305E] mb-2 uppercase tracking-wide">Assessments</h4>
                    <div className="flex flex-wrap gap-2 text-xs">
                      <span className="px-2 py-1 rounded bg-slate-100 text-slate-700 font-semibold">{student.assessments.scored} scored</span>
                      <span className="px-2 py-1 rounded bg-orange-100 text-orange-700 font-semibold" data-testid={`snapshot-dna-${student.student_id}`}>{student.assessments.dna} DNA</span>
                    </div>
                    {student.assessments.averages && (
                      <div className="mt-2">
                        <IndicatorCells averages={student.assessments.averages} testIdPrefix={`snapshot-indicators-${student.student_id}`} />
                      </div>
                    )}
                  </div>
                )}
                {student.last_week && (
                  <div className="px-4 pb-2" data-testid={`snapshot-last-week-${student.student_id}`}>
                    <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3">
                      <h4 className="font-semibold text-sm text-[#00305E] mb-2 uppercase tracking-wide flex items-center gap-2">
                        Last Week Summary
                        <span className="text-[11px] font-normal normal-case text-gray-500">{student.last_week.range.start} → {student.last_week.range.end}</span>
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                        <div className="bg-white rounded border p-2">
                          <div className="flex justify-between mb-1"><span className="text-gray-600 font-medium">Assessments</span><TrendBadge dir={student.last_week.trend.assessments} /></div>
                          <div className="text-gray-700 mb-2">
                            {student.last_week.assessments.scored} scored · {student.last_week.assessments.dna} DNA
                          </div>
                          <IndicatorCells
                            averages={student.last_week.assessments.averages}
                            prev={student.last_week.prior_assessments.averages}
                            testIdPrefix={`snapshot-lw-indicators-${student.student_id}`}
                          />
                        </div>
                        <div className="bg-white rounded border p-2">
                          <div className="flex justify-between mb-1"><span className="text-gray-600 font-medium">Attendance</span><TrendBadge dir={student.last_week.trend.attendance} /></div>
                          <div className="text-gray-700">
                            {student.last_week.attendance.present}/{student.last_week.attendance.expected} present
                            {student.last_week.attendance.pct != null && <> · <b>{student.last_week.attendance.pct}%</b></>}
                            <span className="text-gray-400"> (prev {student.last_week.prior_attendance.pct != null ? student.last_week.prior_attendance.pct + "%" : "—"})</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 bg-white rounded border p-2 text-xs" data-testid={`snapshot-brp-${student.student_id}`}>
                        <div className="text-gray-600 font-medium mb-1">Latest Clinical Goal</div>
                        {student.last_week.brp_latest && student.last_week.brp_latest.text ? (
                          <div>
                            <div className="text-gray-400 mb-0.5">{student.last_week.brp_latest.date}{student.last_week.brp_latest.author ? ` — ${student.last_week.brp_latest.author}` : ""}</div>
                            <div className="text-gray-700 whitespace-pre-wrap">{student.last_week.brp_latest.text}</div>
                          </div>
                        ) : (<div className="text-gray-400">No clinical goal on record.</div>)}
                      </div>
                      {student.last_week.general_notes && student.last_week.general_notes.length > 0 && (
                        <div className="mt-2 bg-white rounded border p-2 text-xs" data-testid={`snapshot-general-notes-${student.student_id}`}>
                          <div className="text-gray-600 font-medium mb-1">General Notes Added This Week</div>
                          <div className="space-y-1.5">
                            {student.last_week.general_notes.map((n, i) => (
                              <div key={i}>
                                {n.date && <span className="text-gray-400 mr-1">{n.date}</span>}
                                <span className="text-gray-700 whitespace-pre-wrap">{n.text}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="text-[10px] text-gray-400 mt-1">Trend compares last week to the week before (higher is better).</div>
                    </div>
                  </div>
                )}
                <div className="px-4 pb-4">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="bg-[#2d4a7a] text-white">
                        <th className="px-3 py-2 text-left font-semibold border-r border-[#3d5a8a] min-w-[100px]">Name</th>
                        {snapshotData.behavior_categories.slice(0, 5).map(cat => (<th key={cat} className="px-2 py-2 text-center font-semibold border-r border-[#3d5a8a] text-[10px]">{cat.split(' ').slice(0, 2).join(' ')}</th>))}
                        <th className="px-2 py-2 text-center font-semibold border-r border-[#3d5a8a]">Period</th>
                        <th className="px-2 py-2 text-center font-semibold border-r border-[#3d5a8a]">Days</th>
                        <th className="px-2 py-2 text-center font-semibold border-r border-[#3d5a8a]">Expected</th>
                        <th className="px-2 py-2 text-center font-semibold border-r border-[#3d5a8a]">Hours</th>
                        <th className="px-2 py-2 text-center font-semibold border-r border-[#3d5a8a]">Exp Hrs</th>
                        <th className="px-2 py-2 text-center font-semibold">%</th>
                      </tr></thead>
                      <tbody>
                        <tr className="bg-amber-50/60 border-b font-semibold">
                          <td className="px-3 py-2 border-r border-gray-200">{student.student_name}</td>
                          {snapshotData.behavior_categories.slice(0, 5).map(cat => (<td key={cat} className="px-2 py-2 text-center border-r border-gray-200">{student.behavior[cat]}</td>))}
                          <td className="px-2 py-2 text-center border-r border-gray-200">YTD</td>
                          <td className="px-2 py-2 text-center border-r border-gray-200">{student.fall.days_attended + student.spring.days_attended}</td>
                          <td className="px-2 py-2 text-center border-r border-gray-200">{student.fall.expected_days + student.spring.expected_days}</td>
                          <td className="px-2 py-2 text-center border-r border-gray-200">{(student.fall.total_hours + student.spring.total_hours).toFixed(1)}</td>
                          <td className="px-2 py-2 text-center border-r border-gray-200">{(student.fall.expected_hours + student.spring.expected_hours).toFixed(1)}</td>
                          <td className={`px-2 py-2 text-center font-bold ${(() => { const te = student.fall.expected_days + student.spring.expected_days; const ta = student.fall.days_attended + student.spring.days_attended; const p = te > 0 ? (ta / te * 100) : 0; return p >= 90 ? 'text-green-700' : p >= 70 ? 'text-yellow-700' : 'text-red-700'; })()}`}>
                            {(() => { const te = student.fall.expected_days + student.spring.expected_days; const ta = student.fall.days_attended + student.spring.days_attended; return te > 0 ? (ta / te * 100).toFixed(1) + '%' : '-'; })()}
                          </td>
                        </tr>
                        {isSummer ? (
                          <tr className="bg-white">
                            <td className="px-3 py-2 text-muted-foreground border-r border-gray-200 pl-6">Summer</td>
                            <td className="px-2 py-2 text-center border-r border-gray-200 text-muted-foreground" colSpan="5"></td>
                            <td className="px-2 py-2 text-center border-r border-gray-200 text-muted-foreground">Summer</td>
                            <td className="px-2 py-2 text-center border-r border-gray-200">{student.summer.days_attended}</td><td className="px-2 py-2 text-center border-r border-gray-200">{student.summer.expected_days}</td>
                            <td className="px-2 py-2 text-center border-r border-gray-200">{student.summer.total_hours.toFixed(1)}</td><td className="px-2 py-2 text-center border-r border-gray-200">{student.summer.expected_hours.toFixed(1)}</td>
                            <td className="px-2 py-2 text-center">{student.summer.attendance_pct.toFixed(1)}%</td>
                          </tr>
                        ) : (<>
                        <tr className="bg-gray-50 border-b">
                          <td className="px-3 py-2 text-muted-foreground border-r border-gray-200 pl-6">Fall</td>
                          <td className="px-2 py-2 text-center border-r border-gray-200 text-muted-foreground" colSpan="5"></td>
                          <td className="px-2 py-2 text-center border-r border-gray-200 text-muted-foreground">Fall</td>
                          <td className="px-2 py-2 text-center border-r border-gray-200">{student.fall.days_attended}</td><td className="px-2 py-2 text-center border-r border-gray-200">{student.fall.expected_days}</td>
                          <td className="px-2 py-2 text-center border-r border-gray-200">{student.fall.total_hours.toFixed(1)}</td><td className="px-2 py-2 text-center border-r border-gray-200">{student.fall.expected_hours.toFixed(1)}</td>
                          <td className="px-2 py-2 text-center">{student.fall.attendance_pct.toFixed(1)}%</td>
                        </tr>
                        <tr className="bg-white">
                          <td className="px-3 py-2 text-muted-foreground border-r border-gray-200 pl-6">Spring</td>
                          <td className="px-2 py-2 text-center border-r border-gray-200 text-muted-foreground" colSpan="5"></td>
                          <td className="px-2 py-2 text-center border-r border-gray-200 text-muted-foreground">Spring</td>
                          <td className="px-2 py-2 text-center border-r border-gray-200">{student.spring.days_attended}</td><td className="px-2 py-2 text-center border-r border-gray-200">{student.spring.expected_days}</td>
                          <td className="px-2 py-2 text-center border-r border-gray-200">{student.spring.total_hours.toFixed(1)}</td><td className="px-2 py-2 text-center border-r border-gray-200">{student.spring.expected_hours.toFixed(1)}</td>
                          <td className="px-2 py-2 text-center">{student.spring.attendance_pct.toFixed(1)}%</td>
                        </tr>
                        </>)}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ))}
            {snapshotData.students.length > 1 && (
              <div className="border rounded-lg overflow-hidden" data-testid="snapshot-school-summary">
                <div className="bg-[#0d2240] text-white px-4 py-2 font-bold text-sm tracking-wide">SCHOOL-WIDE ATTENDANCE SUMMARY</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-[#2d4a7a] text-white"><th className="px-3 py-2 text-left font-semibold border-r border-[#3d5a8a] min-w-[140px]">Period</th><th className="px-2 py-2 text-center font-semibold border-r border-[#3d5a8a] text-xs">Students</th><th className="px-2 py-2 text-center font-semibold border-r border-[#3d5a8a] text-xs">Days Attended</th><th className="px-2 py-2 text-center font-semibold border-r border-[#3d5a8a] text-xs">Expected Days</th><th className="px-2 py-2 text-center font-semibold border-r border-[#3d5a8a] text-xs">Total Hours</th><th className="px-2 py-2 text-center font-semibold border-r border-[#3d5a8a] text-xs">Expected Hours</th><th className="px-2 py-2 text-center font-semibold text-xs">Avg Attendance %</th></tr></thead>
                    <tbody>
                      {(() => {
                        const all = snapshotData.students;
                        const sf = all.reduce((a, s) => ({ da: a.da + s.fall.days_attended, ed: a.ed + s.fall.expected_days, th: a.th + s.fall.total_hours, eh: a.eh + s.fall.expected_hours }), { da: 0, ed: 0, th: 0, eh: 0 });
                        const ss = all.reduce((a, s) => ({ da: a.da + s.spring.days_attended, ed: a.ed + s.spring.expected_days, th: a.th + s.spring.total_hours, eh: a.eh + s.spring.expected_hours }), { da: 0, ed: 0, th: 0, eh: 0 });
                        const su = all.reduce((a, s) => ({ da: a.da + s.summer.days_attended, ed: a.ed + s.summer.expected_days, th: a.th + s.summer.total_hours, eh: a.eh + s.summer.expected_hours }), { da: 0, ed: 0, th: 0, eh: 0 });
                        const tDA = sf.da + ss.da, tED = sf.ed + ss.ed, tTH = sf.th + ss.th, tEH = sf.eh + ss.eh;
                        const avg = tED > 0 ? (tDA / tED * 100) : 0, fP = sf.ed > 0 ? (sf.da / sf.ed * 100) : 0, sP = ss.ed > 0 ? (ss.da / ss.ed * 100) : 0;
                        const suP = su.ed > 0 ? (su.da / su.ed * 100) : 0;
                        const c = (p) => p >= 90 ? 'text-green-700' : p >= 70 ? 'text-yellow-700' : 'text-red-700';
                        return (<>
                          <tr className="bg-amber-50/60 border-b font-semibold"><td className="px-3 py-2 border-r border-gray-200">Year to Date</td><td className="px-2 py-2 text-center border-r border-gray-200">{all.length}</td><td className="px-2 py-2 text-center border-r border-gray-200">{tDA}</td><td className="px-2 py-2 text-center border-r border-gray-200">{tED}</td><td className="px-2 py-2 text-center border-r border-gray-200">{tTH.toFixed(1)}</td><td className="px-2 py-2 text-center border-r border-gray-200">{tEH.toFixed(1)}</td><td className={`px-2 py-2 text-center font-bold ${c(avg)}`}>{avg.toFixed(1)}%</td></tr>
                          {isSummer ? (
                          <tr className="bg-white"><td className="px-3 py-2 border-r border-gray-200">Summer</td><td className="px-2 py-2 text-center border-r border-gray-200">{all.filter(s => s.summer.expected_days > 0).length}</td><td className="px-2 py-2 text-center border-r border-gray-200">{su.da}</td><td className="px-2 py-2 text-center border-r border-gray-200">{su.ed}</td><td className="px-2 py-2 text-center border-r border-gray-200">{su.th.toFixed(1)}</td><td className="px-2 py-2 text-center border-r border-gray-200">{su.eh.toFixed(1)}</td><td className={`px-2 py-2 text-center font-bold ${c(suP)}`}>{suP.toFixed(1)}%</td></tr>
                          ) : (<>
                          <tr className="bg-white border-b"><td className="px-3 py-2 border-r border-gray-200">Fall</td><td className="px-2 py-2 text-center border-r border-gray-200">{all.filter(s => s.fall.expected_days > 0).length}</td><td className="px-2 py-2 text-center border-r border-gray-200">{sf.da}</td><td className="px-2 py-2 text-center border-r border-gray-200">{sf.ed}</td><td className="px-2 py-2 text-center border-r border-gray-200">{sf.th.toFixed(1)}</td><td className="px-2 py-2 text-center border-r border-gray-200">{sf.eh.toFixed(1)}</td><td className={`px-2 py-2 text-center font-bold ${c(fP)}`}>{fP.toFixed(1)}%</td></tr>
                          <tr className="bg-white"><td className="px-3 py-2 border-r border-gray-200">Spring</td><td className="px-2 py-2 text-center border-r border-gray-200">{all.filter(s => s.spring.expected_days > 0).length}</td><td className="px-2 py-2 text-center border-r border-gray-200">{ss.da}</td><td className="px-2 py-2 text-center border-r border-gray-200">{ss.ed}</td><td className="px-2 py-2 text-center border-r border-gray-200">{ss.th.toFixed(1)}</td><td className="px-2 py-2 text-center border-r border-gray-200">{ss.eh.toFixed(1)}</td><td className={`px-2 py-2 text-center font-bold ${c(sP)}`}>{sP.toFixed(1)}%</td></tr>
                          </>)}
                        </>);
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent data-testid="email-snapshot-dialog">
          <DialogHeader>
            <DialogTitle>Email Weekly Snapshot</DialogTitle>
            <DialogDescription>
              Sends a branded PDF of this snapshot{scopeDistrictName ? <> for <b>{scopeDistrictName}</b></> : null}
              {selectedStudentObj ? <> ({selectedStudentObj.last_name}, {selectedStudentObj.preferred_name || selectedStudentObj.first_name})</> : null}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="snap-email-to" className="text-sm">Recipient email</Label>
              <Input id="snap-email-to" type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="district.contact@example.com" data-testid="email-snapshot-recipient" />
              {scopeDistrictRec && !scopeDistrictRec.contact_email && <p className="text-xs text-amber-600 mt-1">No saved contact email for this district yet — enter one below.</p>}
            </div>
            <div>
              <Label htmlFor="snap-email-note" className="text-sm">Note (optional)</Label>
              <Textarea id="snap-email-note" rows={3} value={emailNote} onChange={(e) => setEmailNote(e.target.value)} placeholder="Add a short message for the district contact…" data-testid="email-snapshot-note" />
            </div>
            {includeLastWeek && narrowScope && <p className="text-xs text-muted-foreground">The last-week summary is included in the PDF.</p>}
            {scopeDistrictName && (
              <div className="flex items-center gap-2">
                <Checkbox id="snap-save-email" checked={saveDistrictEmail} onCheckedChange={(v) => setSaveDistrictEmail(!!v)} data-testid="email-snapshot-save-district" />
                <Label htmlFor="snap-save-email" className="text-sm cursor-pointer">Save this as {scopeDistrictName}'s contact email</Label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)} disabled={emailSending}>Cancel</Button>
            <Button className="bg-[#00305E] hover:bg-[#00305E]/90" onClick={sendEmail} disabled={emailSending} data-testid="email-snapshot-send-btn">
              <Mail className="h-4 w-4 mr-1.5" />{emailSending ? "Sending…" : "Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <BulkDistrictSnapshotDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        startDate={bulkStartEnd?.start_date}
        endDate={bulkStartEnd?.end_date}
        periodLabel={periodLabelText()}
      />
    </Card>
  );
}
